import { ThinkingStepCodes, type AssistantThinkingStep } from "#features/conversations/types/ThinkingStep.ts";

const normalizeMergeValue = (value?: string) => value?.trim() || null;

const getMergeIdentity = (step: AssistantThinkingStep) => ({
    title: normalizeMergeValue(step.title),
    iconKey: normalizeMergeValue(step.iconKey),
    connectedIntegration: normalizeMergeValue(step.connectedIntegration)
});

export const canMergeThinkingSteps = (currentStep: AssistantThinkingStep, nextStep: AssistantThinkingStep) => {
    const currentIdentity = getMergeIdentity(currentStep);
    const nextIdentity = getMergeIdentity(nextStep);

    // A skill step is identified by the skill it names, so two different skills never collapse into one row.
    if (currentStep.code === ThinkingStepCodes.skillLoaded || nextStep.code === ThinkingStepCodes.skillLoaded) {
        return currentStep.code === nextStep.code && currentIdentity.title === nextIdentity.title;
    }

    const hasExplicitMergeIdentity = Boolean(
        currentIdentity.iconKey || nextIdentity.iconKey ||
        currentIdentity.connectedIntegration || nextIdentity.connectedIntegration
    );

    if (hasExplicitMergeIdentity) {
        return (
            currentIdentity.title === nextIdentity.title &&
            currentIdentity.iconKey === nextIdentity.iconKey &&
            currentIdentity.connectedIntegration === nextIdentity.connectedIntegration
        );
    }

    return currentStep.code === nextStep.code;
};

const finalizeGroup = (step: AssistantThinkingStep, groupedDetails: string[]): AssistantThinkingStep => {
    if (groupedDetails.length === 0) {
        return step;
    }

    return {
        ...step,
        detail: groupedDetails.join("\n\n---\n\n")
    };
};

export const mergeThinkingSteps = (thinkingSteps: AssistantThinkingStep[]): AssistantThinkingStep[] => {
    if (thinkingSteps.length === 0) {
        return [];
    }

    const merged: AssistantThinkingStep[] = [];
    let currentGroup: AssistantThinkingStep | null = null;
    const groupedDetails: string[] = [];

    for (const step of thinkingSteps) {
        if (currentGroup && canMergeThinkingSteps(currentGroup, step)) {
            const currentStep: AssistantThinkingStep = currentGroup;

            if (step.detail && step.detail.trim()) {
                groupedDetails.push(step.detail.trim());
            }

            const mergedSourceTitles: string[] = Array.from(new Set([
                ...(currentStep.sourceTitles || []),
                ...(step.sourceTitles || [])
            ]));
            const mergedSourceChannels: NonNullable<AssistantThinkingStep["sourceChannels"]> = Array.from(new Set([
                ...(currentStep.sourceChannels || []),
                ...(step.sourceChannels || [])
            ]));

            currentGroup = {
                ...currentStep,
                state: step.state,
                title: currentStep.title ?? step.title,
                iconKey: currentStep.iconKey ?? step.iconKey,
                connectedIntegration: currentStep.connectedIntegration ?? step.connectedIntegration,
                sourceCount: step.sourceCount ?? currentStep.sourceCount,
                sourceTitles: mergedSourceTitles,
                sourceChannels: mergedSourceChannels
            };

            continue;
        }

        if (currentGroup) {
            merged.push(finalizeGroup(currentGroup, groupedDetails));
        }

        currentGroup = { ...step };
        groupedDetails.length = 0;

        if (step.detail && step.detail.trim()) {
            groupedDetails.push(step.detail.trim());
        }
    }

    if (currentGroup) {
        merged.push(finalizeGroup(currentGroup, groupedDetails));
    }

    return merged;
};
